import React from 'react'
import {
  Container,
  Grid,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Paper,
  Fade,
  Skeleton
} from '@mui/material'
import { Search, FilterList, Sort } from '@mui/icons-material'
import { useGetPoemsQuery } from '../../../store/apiSlice'
import type { Poem } from '../../../store/poemsSlice'
import type { RootState } from '../../../store/store'
import PoemCard from '../components/PoemCard'
import SEOHead from '../../../shared/components/SEOHead'
import { SITE_URL } from '../../../config/constants'
import AdSenseManager from '../../../shared/components/AdSenseManager'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const ExplorePoems: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: items, isLoading: loading, error: fetchError } = useGetPoemsQuery()
  const error = fetchError ? 'Failed to load poems' : null

  const safeItems = React.useMemo(() => (Array.isArray(items) ? items : []), [items])

  const [searchTerm, setSearchTerm] = React.useState('')
  const [selectedCategory, setSelectedCategory] = React.useState('')
  const [sortBy, setSortBy] = React.useState<'date' | 'title' | 'author'>('date')

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const categories = React.useMemo(() => {
    const cats = new Set<string>()
    safeItems.forEach(poem => {
      if (poem.category) cats.add(poem.category)
    })
    return Array.from(cats)
  }, [safeItems])

  const filteredPoems = React.useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    const filtered = safeItems
      .filter(poem => poem.published !== false)
      .filter(poem => {
        if (!normalizedSearch) return true
        const target = [poem.title, poem.author, poem.content]
          .filter(Boolean)
          .join(' ') 
          .toLowerCase()
        return target.includes(normalizedSearch)
      })
      .filter(poem => (selectedCategory ? poem.category === selectedCategory : true))

    const sorted = [...filtered]
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title)
        case 'author':
          return (a.author || '').localeCompare(b.author || '')
        case 'date':
        default:
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return bDate - aDate
      }
    })

    return sorted
  }, [safeItems, searchTerm, selectedCategory, sortBy])

  const renderGrid = () => {
    if (loading && !safeItems.length) {
      return (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      )
    }

    if (error) {
      return (
        <Typography color="error" sx={{ mt: 4 }}>
          {error}
        </Typography>
      )
    }

    if (!filteredPoems.length) {
      return (
        <Typography color="text.secondary" sx={{ mt: 4 }}>
          {t('noPoemsFound')}
        </Typography>
      )
    }

    return (
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {filteredPoems.map(poem => (
          <Grid item xs={12} sm={6} md={4} key={poem.id}>
            <Fade in timeout={600}>
              <Box>
                <PoemCard poem={poem} onClick={() => navigate(`/poem/${poem.id}`)} />
              </Box>
            </Fade>
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <>
      <SEOHead
        title={t('explore.title', 'Explore Poems – Kavithai.de')}
        description={t('explore.description', 'Browse the complete collection of Tamil poems on Kavithai.de')}
        url={`${SITE_URL}/poems`}
        type="website"
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h2" sx={{ fontWeight: 800, textAlign: 'center', mb: 2 }}>
          {t('explore.heading', 'Explore Poems')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mb: 6 }}>
          {t('explore.subheading', 'Dive into our curated collection of classic and contemporary Tamil poetry.')}
        </Typography>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 5 }} elevation={1}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder={t('explore.searchPlaceholder', 'Search poems by title, author, or text')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="explore-category-label">
                  <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('filter')}
                </InputLabel>
                <Select
                  labelId="explore-category-label"
                  label={t('filter')}
                  value={selectedCategory}
                  onChange={event => setSelectedCategory(event.target.value)}
                >
                  <MenuItem value="">{t('categories.all', 'All Categories')}</MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      <Chip label={category} size="small" />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="explore-sort-label">
                  <Sort sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('sort')}
                </InputLabel>
                <Select
                  labelId="explore-sort-label"
                  label={t('sort')}
                  value={sortBy}
                  onChange={event => setSortBy(event.target.value as typeof sortBy)}
                >
                  <MenuItem value="date">{t('explore.sort.recent', 'Newest')}</MenuItem>
                  <MenuItem value="title">{t('explore.sort.title', 'Title')}</MenuItem>
                  <MenuItem value="author">{t('explore.sort.author', 'Author')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {renderGrid()}
      </Container>

      <Box sx={{ backgroundColor: 'background.default', py: 4 }}>
        <Container maxWidth="lg">
          <AdSenseManager page="home" position="footer" />
        </Container>
      </Box>
    </>
  )
}

export default ExplorePoems
